import { ActionIcon, Badge, Button, Group, Menu, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconDots, IconKey, IconMail, IconPencil, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { errorMessage, type User } from "@/api";
import { type Column, confirmAction, DataTable, DateUTC, PageHeader, QueryState, StatusPill } from "@/components";
import { ROLE_LABELS, type Role } from "@/shell/roles";
import { useMe } from "@/shell/session";

import { useResetStaffTotpMutation, useStaffPasswordResetMutation, useStaffQuery } from "./api";
import { StaffFormModal } from "./StaffFormModal";

const LIMIT = 50;

/** Admin → Staff (superadmin). Template screen: query + table + modal form + confirmed actions. */
export function StaffPage() {
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const offset = Number(params.get("offset") ?? 0) || 0;
  const query = useStaffQuery(LIMIT, offset);
  const resetTotp = useResetStaffTotpMutation();
  const resetPassword = useStaffPasswordResetMutation();
  const [target, setTarget] = useState<User | "new" | null>(null);

  const run = async (action: () => Promise<unknown>, done: string) => {
    try {
      await action();
      notifications.show({ color: "green", message: done });
    } catch (e) {
      notifications.show({ color: "red", message: errorMessage(e) });
    }
  };

  const onResetTotp = async (u: User) => {
    const ok = await confirmAction({
      title: "Reset two-factor authentication?",
      consequence: `${u.email}'s authenticator stops working and they must enrol a new one at their next sign-in.`,
      confirmLabel: "Reset 2FA",
    });
    if (ok)
      await run(() => resetTotp.mutateAsync(u.public_id ?? ""), "2FA reset. They will enrol again at next sign-in.");
  };

  const onResetPassword = async (u: User) => {
    const ok = await confirmAction({
      title: "Send a password reset link?",
      consequence: `${u.email} receives an email link to set a new password; their current password keeps working until then.`,
      confirmLabel: "Send link",
      danger: false,
    });
    if (ok) await run(() => resetPassword.mutateAsync(u.public_id ?? ""), "Reset link sent.");
  };

  const columns: Column<User>[] = [
    {
      key: "email",
      header: "Email",
      sortValue: (u) => u.email,
      render: (u) => (
        <Group gap={6} wrap="nowrap">
          <Text size="sm">{u.email}</Text>
          {u.public_id === me?.public_id && (
            <Badge size="xs" variant="outline">
              you
            </Badge>
          )}
        </Group>
      ),
    },
    {
      key: "name",
      header: "Name",
      sortValue: (u) => u.display_name,
      render: (u) => u.display_name || <Text c="dimmed">—</Text>,
    },
    {
      key: "roles",
      header: "Roles",
      render: (u) => (
        <Group gap={4}>
          {(u.roles ?? []).map((r) => (
            <Badge key={r} size="sm" variant="light">
              {ROLE_LABELS[r as Role] ?? r}
            </Badge>
          ))}
        </Group>
      ),
    },
    { key: "status", header: "Status", sortValue: (u) => u.status, render: (u) => <StatusPill status={u.status} /> },
    {
      key: "totp",
      header: "2FA",
      sortValue: (u) => String(u.totp_enabled),
      render: (u) => <StatusPill status={u.totp_enabled ? "enabled" : "pending"} />,
    },
    {
      key: "last",
      header: "Last sign-in",
      sortValue: (u) => u.last_login_at ?? "",
      render: (u) => <DateUTC value={u.last_login_at} local />,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (u) => (
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" aria-label={`Actions for ${u.email}`} data-testid="row-actions">
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => setTarget(u)} data-testid="action-edit">
              Edit
            </Menu.Item>
            <Menu.Item
              leftSection={<IconKey size={14} />}
              onClick={() => onResetTotp(u)}
              data-testid="action-reset-2fa"
            >
              Reset 2FA
            </Menu.Item>
            <Menu.Item
              leftSection={<IconMail size={14} />}
              onClick={() => onResetPassword(u)}
              data-testid="action-reset-password"
            >
              Send password reset
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Staff"
        description="Back-office staff accounts and their roles."
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => setTarget("new")} data-testid="staff-new">
            New staff user
          </Button>
        }
      />
      <QueryState query={query}>
        {(rows) => (
          <DataTable
            testId="staff-table"
            columns={columns}
            rows={rows}
            rowKey={(u) => u.public_id ?? u.email ?? ""}
            loading={query.isFetching}
            paging={{ limit: LIMIT, offset, onChange: (o) => setParams(o ? { offset: String(o) } : {}) }}
          />
        )}
      </QueryState>
      <StaffFormModal target={target} onClose={() => setTarget(null)} />
    </>
  );
}
